import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { calculateTotalNumberOfItemsInBasket } from '@common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import { Spinner } from '@common/components/Spinner/Spinner';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@lang';
import { useQueryClient } from '@tanstack/react-query';
import { some } from '@utils/some';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { FormWrapper, InnerScroller, Modal } from '@common/components/BookingForms';
import { StickyBarWrapper } from '@common/components/StickyBar';
import { Routes } from '@common/constants';
import { useInvalidatePackagesAvailability } from '../packages';
import { useRoomExtras } from '../rooms';
import {
  useGetSpaPackage,
  useGetSpaPackageConfigGroups,
  useGetSpaPackageTimeslots,
  useGetSpaSettings,
  useInvalidateSpaPackageConfigGroups,
} from '../spa';
import { useGetSpaPackageExtras } from '../spa/hooks/useGetSpaPackageExtras';
import { useStepNavigation } from './components/StepNavigator/useStepNavigation';
import { STICKY_BAR_ID } from './constants';
import {
  DynamicPackagesConfigurationWizardDispatchProps,
  DynamicPackagesConfigurationWizardProps,
  DynamicPackagesConfigurationWizardStoreProps,
} from './DynamicPackagesConfigurationWizard.types';
import { FormSteps } from './FormSteps/FormSteps';
import { Header } from './FormSteps/FormSteps.styles';
import { addToBasket } from './helpers/addToBasket';
import { getExperienceSteps } from './helpers/getExperienceSteps';
import { getTablesSteps } from './helpers/getTablesSteps';
import { mapLoggedInUserToGuest } from './helpers/mapLoggedInUserToGuest';
import { FormStep, PackagesFormValues, PackagesSchema } from './types';
import { useGetPackage } from './useGetPackage';
import { PackageConfigurationProvider, WizardNavigationProvider } from './contexts';

type DynamicPackagesConfigProps = DynamicPackagesConfigurationWizardStoreProps &
  DynamicPackagesConfigurationWizardProps &
  DynamicPackagesConfigurationWizardDispatchProps;

export const DynamicPackagesConfigurationWizard: FC<DynamicPackagesConfigProps> = ({
  property,
  journeyParams,
  rate,
  room,
  loggedGuest,
  basketGuests,
  reservedSpaTimeSlots,
  updateBasket,
  createBasket,
  basketDetails,
  language,
}) => {
  //stay info
  const adults = journeyParams.adults;
  const children = journeyParams.children;
  const totalGuestAmount = adults + children;
  const roomId = room.id;
  const rateId = rate.id;
  const { t } = useTranslations();
  const fromDate = journeyParams.fromDate.format('YYYY-MM-DD');
  const toDate = journeyParams.toDate
    ? journeyParams.toDate.format('YYYY-MM-DD')
    : journeyParams.fromDate.add(1, 'days').format('YYYY-MM-DD');
  const [isModalOpen, setIsModalOpen] = useState(true);
  const queryClient = useQueryClient();

  //get data for the package
  const {
    data: packageData,
    isLoading: isFetching,
    isError,
  } = useGetPackage({
    propertyUuid: property?.uuid ?? '',
    rateId: rate.uuid ?? '',
  });

  const stayDuration =
    packageData && packageData.numberOfNights !== 1
      ? packageData.numberOfDays
      : moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days');

  // A package is "repeating" when it's designed for 1 night but the customer books multiple nights
  // experiences from day 0/1 repeat across the stay so experiences mapped to day 0 are available on all days excluding checkout
  // experiences mapped to day 1 are available on all days except day 0
  const isPackageRepeating = packageData?.numberOfNights === 1;

  const { data: availableExtras } = useRoomExtras({
    ...journeyParams,
    fromDate,
    toDate,
    roomId,
    rateId,
  });

  const { data: spaDay } = useGetSpaPackage(
    property.id,
    Number(packageData?.meta?.entityId),
    language,
    true
  );

  const { data: timeslots } = useGetSpaPackageTimeslots(
    property.id,
    Number(packageData?.meta?.entityId),
    moment(journeyParams.fromDate).format('YYYY-MM-DD'),
    totalGuestAmount,
    stayDuration
  );

  const { data: spaExtras = [] } = useGetSpaPackageExtras(
    property.id,
    Number(packageData?.meta?.entityId),
    property.defaultLanguage,
    moment(journeyParams.fromDate).format('YYYY-MM-DD'),
    true
  );

  const timeslot = Object.values(timeslots || [])?.[0]?.[0];
  const datesArray = new Array(stayDuration)
    .fill({})
    .map((_, i) => moment(journeyParams.fromDate).add(i, 'days').format('YYYY-MM-DD'));
  datesArray.forEach((date) => {
    useGetSpaPackageConfigGroups(
      spaDay?.configurationGroups,
      property.id,
      Number(packageData?.meta?.entityId),
      date,
      totalGuestAmount,
      timeslot?.startTime,
      timeslot?.endTime
    );

    useGetSpaPackageConfigGroups(
      spaDay?.configurationGroups,
      property.id,
      Number(packageData?.meta?.entityId),
      date,
      1,
      timeslot?.startTime,
      timeslot?.endTime
    );
  });

  //route handling
  const router = useRouter();
  useEffect(() => {
    if (isError) {
      router.push({
        pathname: Routes.RATE_BOOKING,
        query: { id: rateId, bookingStep: 'guestDetailsStep', room: roomId },
      });
    }
  }, [isError]);

  //form management
  const form = useForm<PackagesFormValues>({
    resolver: zodResolver(PackagesSchema),
    defaultValues: {
      experiences: {
        guestsHaveDifferentTimes: true,
      },
    },
  });
  const { setValue, watch } = form;

  //form submission
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showConfirmation = () => {
    setIsModalOpen(false);
    setIsSubmitting(false);
    setIsConfirmationVisible(true);
    useInvalidatePackagesAvailability(queryClient);
    useInvalidateSpaPackageConfigGroups(queryClient);
  };
  const hideConfirmation = () => {
    router.push({
      pathname: Routes.RATE_DETAIL,
      query: {
        id: rate.id,
      },
    });
    setIsConfirmationVisible(false);
  };

  const bookingDetails = watch();
  //step management
  const { steps: experienceSteps, includedExperiences } = getExperienceSteps(
    packageData?.experiences || [],
    stayDuration,
    isPackageRepeating
  );

  const selectedExperiences = watch('selectedExperiences');

  useEffect(() => {
    if (includedExperiences && !selectedExperiences) {
      setValue('selectedExperiences', includedExperiences);
    }
  }, [includedExperiences, selectedExperiences]);

  const allFormSteps: FormStep[] = [
    {
      step: 'guestDetailsStep',
      type: 'rooms',
    },
    ...(some(availableExtras) ? [{ step: 'roomExtrasStep', type: 'rooms' }] : []),
    ...experienceSteps,
    ...(some(spaExtras) ? [{ step: 'spaExtrasStep', type: 'spa' }] : []),
    ...getTablesSteps(packageData?.restaurants || [], isPackageRepeating, stayDuration),
  ];
  const { stepNavigatorComponent, goToNextStep, isLastStep, currentFormStep } = useStepNavigation(
    rate.uuid || '',
    rate.id || '',
    property.uuid || '',
    allFormSteps
  );

  const handleAddToBasket = () => {
    setIsSubmitting(true);
    addToBasket({
      createBasket,
      updateBasket,
      bookingDetails,
      roomRateEntity: rate,
      roomId,
      children,
      childAges: journeyParams.childAges,
      fromDate,
      toDate,
      property,
      language,
      basketDetails,
      showConfirmation,
      spaPackageId: packageData?.meta?.entityId || '',
      spaOpenTime: timeslot?.startTime || '',
      spaCloseTime: timeslot?.endTime || '',
      maxGuestsPerExperience: spaDay?.numberOfGuests || totalGuestAmount,
      availableExtras: availableExtras || [],
      loggedInUserGuestData: mapLoggedInUserToGuest(loggedGuest),
      isPackageRepeating: isPackageRepeating,
      promoRateCode: journeyParams.promoRateCode,
    });
  };

  const packageConfigValue = {
    property,
    journeyParams,
    stayDuration,
    totalGuestAmount,
    isPackageRepeating,
    spaPackageId: packageData?.meta?.entityId || '',
    spaOpenTime: timeslot?.startTime || '',
    spaCloseTime: timeslot?.endTime || '',
    rate,
    room,
    availableExtras,
    basketGuests: basketGuests || [],
    loggedInGuest: loggedGuest,
    reservedSpaTimeSlots: reservedSpaTimeSlots || [],
  };

  const wizardNavigationValue = {
    currentFormStep,
    allFormSteps,
    handleNextStep: () => {
      if (!isLastStep) {
        goToNextStep();
      } else {
        handleAddToBasket();
      }
    },
    isLastStep,
    stickyBarId: STICKY_BAR_ID,
  };

  const formStepsProps = {
    form,
    isSubmitting,
    addToBasket: handleAddToBasket,
  };

  //basket drawer
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;
  const showBasketDrawer = isConfirmationVisible && basketDetails;

  const modules = Object.keys(property.modulesExcludingDisabled);

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
    items: basketDetails?.items ?? [],
  });
  return (
    <div>
      {showBasketDrawer && (
        <BasketDrawer
          currencyCode={basketDetails.total.currencyCode}
          numberOfItems={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
          open={true}
          onClose={hideConfirmation}
          title={t('basketDrawer.title')}
          totalCost={basketDetails.total.amount}
          items={basketDetails.items}
          modules={modulesToRecommend}
          hideArrivalTimes={hideArrivalTimes}
        />
      )}
      <Modal isVisible={isSubmitting || (isModalOpen && !isError)}>
        <>
          <Spinner showDelayMessage isActive={isSubmitting || (isFetching && !packageData)} />
          <Header>{stepNavigatorComponent}</Header>
          <InnerScroller>
            <FormWrapper>
              <PackageConfigurationProvider value={packageConfigValue}>
                <WizardNavigationProvider value={wizardNavigationValue}>
                  <FormSteps {...formStepsProps} />
                </WizardNavigationProvider>
              </PackageConfigurationProvider>
            </FormWrapper>
          </InnerScroller>
        </>
      </Modal>
      <StickyBarWrapper id={STICKY_BAR_ID} zIndex={9999} />
    </div>
  );
};
